import { Row, Col } from 'react-dsgov';
import styles from './itemReceitaBrutaCalculada.module.scss';
import {
  INCMReceitaBrutaCalculada,
  INCMReceitaBrutaCalculadaPrecoCorrente,
  INCMReceitaBrutaCalculadaValorReferencia,
} from '../../../../../../../../../../../interfaces/IReceitaBrutaCalculada';
import { EDiscriminadorMetodoReceitaBrutaCalculada } from '../../../../../../../../../../../enums/EMetodoBaseCalculo';
import { formatCurrency } from '../../../../../../../../../../../utils/formatCurrency';
import { BrDivider } from '@govbr-ds/react-components';

interface IItemReceitaBrutaCalculadaProps {
  itemNCM: INCMReceitaBrutaCalculada;
}

const ItemReceitaBrutaCalculada = ({ itemNCM }: IItemReceitaBrutaCalculadaProps) => {
  const isValorReferencia = EDiscriminadorMetodoReceitaBrutaCalculada?.VALOR_REFERENCIA in itemNCM;

  const formatarFloat = (value: number) => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  };

  const formatarQuantidade = (value: number) => {
    return value.toLocaleString('pt-BR');
  };

  const formatarBoolean = (value: boolean) => {
    return value ? 'Sim' : 'Não';
  };

  const formatarTipoMetodo = () => {
    if (isValorReferencia) {
      return 'Valor Referência';
    } else {
      return 'Preço Corrente';
    }
  };

  const htmlCampoPrecoOuValor = () => {
    if (isValorReferencia) {
      const ncmValorReferencia = itemNCM as INCMReceitaBrutaCalculadaValorReferencia;

      return (
        <Col>
          <span className={styles.label}>Valor de Produção (VP)</span>
          <p className={styles.text}>{formatCurrency(ncmValorReferencia?.valorProducaoTotal)}</p>
        </Col>
      );
    } else {
      const ncmPrecoCorrente = itemNCM as INCMReceitaBrutaCalculadaPrecoCorrente;

      return (
        <Col>
          <span className={styles.label}>Preço Corrente (R$)</span>
          <p className={styles.text}>{formatCurrency(ncmPrecoCorrente?.precoCorrente)}</p>
        </Col>
      );
    }
  };

  const htmlRegistrosValorReferencia = () => {
    const ncmValorReferencia = itemNCM as INCMReceitaBrutaCalculadaValorReferencia;

    return (
      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>% Teor Concentrado (TC)</span>
          <p className={styles.text}>{formatarFloat(ncmValorReferencia?.teorConcentrado)}</p>
        </Col>

        <Col>
          <span className={styles.label}>% Teor Alimentação (TA)</span>
          <p className={styles.text}>{formatarFloat(ncmValorReferencia?.teorAlimentacao)}</p>
        </Col>

        <Col>
          <span className={styles.label}>Substância (Fatores de Ajuste)</span>
          <p className={styles.text}>{ncmValorReferencia?.substanciaFatorAjuste?.nome ?? 'Não se aplica'}</p>
        </Col>
      </Row>
    );
  };

  const htmlBaseCalculo = () => {
    const CAMPOS_CALCULO_BASE = () => (
      <>
        <div>
          <span className={styles.label}>Receita Bruta Calculada (R$)</span>
          <p className={styles.text}>{formatCurrency(itemNCM?.valorReceitaBrutaCalculada)}</p>
        </div>

        <div>
          <span className={styles.label}>Deduções (R$)</span>
          <p className={styles.text}>{formatCurrency(itemNCM?.valorDeducoes ?? '')}</p>
        </div>

        <div>
          <span className={styles.label}>Base de Cálculo (R$)</span>
          <p className={styles.text}>{formatCurrency(itemNCM?.valorBaseCalculo)}</p>
        </div>
      </>
    );

    if (isValorReferencia) {
      const ncmValorReferencia = itemNCM as INCMReceitaBrutaCalculadaValorReferencia;

      return (
        <div className={styles.infoContainer}>
          <div>
            <span className={styles.label}>Índice de Enriquecimento</span>
            <p className={styles.text}>{formatarFloat(ncmValorReferencia?.indiceEnriquecimento)}</p>
          </div>
          <div>
            <span className={styles.label}>Fator de Ajuste</span>
            <p className={styles.text}>{formatarFloat(ncmValorReferencia?.fatorAjuste)}</p>
          </div>
          <div>
            <span className={styles.label}>Receita Bruta Calculada (R$)</span>
            <p className={styles.text}>{formatCurrency(itemNCM?.valorReceitaBrutaCalculada)}</p>
          </div>
          <div>
            <span className={styles.label}>Valor de Referência Unitário (R$)</span>
            <p className={styles.text}>{formatCurrency(ncmValorReferencia?.valorReferenciaUnitario)}</p>
          </div>
          <div>
            <span className={styles.label}>Deduções (R$)</span>
            <p className={styles.text}>{formatCurrency(itemNCM?.valorDeducoes ?? '')}</p>
          </div>
          <div>
            <span className={styles.label}>Base de Cálculo (R$)</span>
            <p className={styles.text}>{formatCurrency(itemNCM?.valorBaseCalculo)}</p>
          </div>
        </div>
      );
    } else {
      return <div className={styles.infoContainer}>{CAMPOS_CALCULO_BASE()}</div>;
    }
  };

  return (
    <>
      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>Método</span>
          <p className={styles.text} data-testid='metodo'>
            {formatarTipoMetodo()}
          </p>
        </Col>
      </Row>

      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>Código NCM do Bem Mineral</span>
          <p className={styles.text}>{itemNCM?.ncmBemMineral.codigo}</p>
        </Col>

        <Col>
          <span className={styles.label}>Unidade de Medida</span>
          <p className={styles.text}>{itemNCM?.unidadeMedida}</p>
        </Col>

        <Col>
          <span className={styles.label}>Fase de Industrialização</span>
          <p className={styles.textArea}>{itemNCM?.faseDeIndustrializacao}</p>
        </Col>
      </Row>

      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>Nova Espécie</span>
          <p className={styles.textArea}>{itemNCM?.novaEspecie}</p>
        </Col>

        <Col>
          <span className={styles.label}>Código NCM da Nova Espécie</span>
          <p className={styles.text}>{itemNCM?.ncmNovaEspecie.codigo + ' - ' + itemNCM?.ncmNovaEspecie.descricao}</p>
        </Col>

        <Col>
          <span className={styles.label}>Unidade de Medida da Nova Espécie</span>
          <p className={styles.text}>{itemNCM?.unidadeMedidaNovaEspecie}</p>
        </Col>
      </Row>

      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>Quantidade consumida na obtenção de nova espécie</span>
          <p className={styles.text}>{formatarQuantidade(itemNCM?.quantidadeNovaEspecie)}</p>
        </Col>

        <Col>
          <span className={styles.label}>Quantidade utilizada, doada ou bonificada</span>
          <p className={styles.text}>{formatarQuantidade(itemNCM?.quantidadeUtilizadaDoadaOuBonificada)}</p>
        </Col>

        {htmlCampoPrecoOuValor()}
      </Row>

      {isValorReferencia && htmlRegistrosValorReferencia()}

      <Row className={styles.row}>
        <Col>
          <span className={styles.label}>O bem mineral é oriundo de rejeitos?</span>
          <p className={styles.text}>{formatarBoolean(itemNCM?.isRejeito)}</p>
        </Col>
        <Col>
          <span className={styles.label}>A substância possui uso específico?</span>
          <p className={styles.text}>{formatarBoolean(itemNCM?.hasUsoEspecifico)}</p>
        </Col>
      </Row>

      <div className={styles.movimentacao}>
        {itemNCM.tributosValores && itemNCM.tributosValores.length > 0 && (
          <>
            <Row>
              {itemNCM.tributosValores?.map((item) => (
                <Col key={item.nome} className={styles.tributosContainer}>
                  <span className={styles.label}>{item.nome}</span>
                  <p className={styles.text}>{formatCurrency(item.valor || '')}</p>
                </Col>
              ))}
            </Row>
            <div className={styles.metodoDivider}>
              <BrDivider />
            </div>
          </>
        )}

        <div className={styles.infoContainer}>
          <div>
            <span className={styles.label}>Quantidade a abater da aquisição de terceiros</span>
            <p className={styles.text}>{formatarQuantidade(itemNCM?.quantidadeAquisicaoTerceiros)}</p>
          </div>

          <div>
            <span className={styles.label}>Valor total a abater da aquisição de terceiros (R$)</span>
            <p className={styles.text}>{formatCurrency(itemNCM?.valorAquisicaoTerceiros)}</p>
          </div>
        </div>

        {htmlBaseCalculo()}
      </div>
    </>
  );
};

export default ItemReceitaBrutaCalculada;
